import React from 'react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Check, CreditCard, Zap, Shield, Crown } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

export function SubscriptionsPage() {
  const { user } = useAuth();
  const currentPlan = user?.brokerProfile?.subscriptionPlan || 'free';

  return (
    <DashboardLayout>
      <div className="space-y-12">
        <div>
          <h1 className="text-4xl font-display font-black uppercase text-ink italic">Mi Suscripción</h1>
          <p className="text-serif italic text-ink/60 mt-2">Gestiona tu plan y accede a herramientas premium.</p>
        </div>

        {/* Current Plan Status */}
        <div className="brutalist-border bg-ink text-parchment p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <Crown className="h-64 w-64" />
          </div>
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="bg-terracotta text-parchment px-3 py-1 text-[10px] font-black uppercase tracking-[0.4em] mb-6 inline-block">Estado Actual</span>
              <h2 className="text-5xl font-display font-black uppercase italic mb-4">Plan {currentPlan}</h2>
              <p className="font-serif italic text-parchment/60 text-lg">Tu suscripción está activa hasta el 15 de Enero, 2026.</p>
            </div>
            <div className="flex flex-col md:items-end gap-4">
              <Button variant="terracotta" size="lg" className="h-16 px-12 text-lg">MEJORAR PLAN</Button>
              <button className="text-[10px] font-black uppercase tracking-widest text-parchment/40 hover:text-parchment transition-colors">CANCELAR SUSCRIPCIÓN</button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Method of Payment */}
          <div className="lg:col-span-1 brutalist-border bg-parchment p-8 space-y-8">
            <h3 className="text-xs font-black uppercase tracking-[0.3em] text-terracotta">Método de Pago</h3>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-ink/5 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-ink" />
              </div>
              <div>
                <p className="text-sm font-black uppercase italic">VISA **** 4242</p>
                <p className="text-[10px] font-black uppercase tracking-widest opacity-40">Expira: 12/28</p>
              </div>
            </div>
            <Button variant="outline" className="w-full border-2">CAMBIAR TARJETA</Button>
          </div>

          {/* Billing History */}
          <div className="lg:col-span-2 brutalist-border bg-parchment p-8 space-y-8">
            <h3 className="text-xs font-black uppercase tracking-[0.3em] text-terracotta">Historial de Facturación</h3>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex justify-between items-center border-b border-ink/5 pb-4">
                  <div>
                    <p className="text-sm font-black uppercase italic">Factura #INV-00{i}</p>
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-40">01 Dic, 2025</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-black uppercase italic">CLP 49.990</p>
                    <button className="text-[10px] font-black uppercase tracking-widest text-terracotta hover:underline">DESCARGAR PDF</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Plan Comparison Shortcut */}
        <div className="pt-12 border-t-2 border-ink">
          <h3 className="text-xl font-display font-black uppercase italic mb-8">Compara los beneficios de tu plan</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 bg-moss/5 border border-moss/20">
              <Zap className="h-6 w-6 text-moss mb-4" />
              <h4 className="text-sm font-display font-black uppercase mb-2">Tasación IA</h4>
              <p className="text-xs font-serif italic text-ink/60">Ilimitadas en tu plan actual.</p>
            </div>
            <div className="p-6 bg-terracotta/5 border border-terracotta/20">
              <Crown className="h-6 w-6 text-terracotta mb-4" />
              <h4 className="text-sm font-display font-black uppercase mb-2">Soporte Premium</h4>
              <p className="text-xs font-serif italic text-ink/60">Acceso directo a abogados y contadores.</p>
            </div>
            <div className="p-6 bg-ink/5 border border-ink/20">
              <Shield className="h-6 w-6 text-ink mb-4" />
              <h4 className="text-sm font-display font-black uppercase mb-2">Marca Blanca</h4>
              <p className="text-xs font-serif italic text-ink/60">Disponible solo en plan Enterprise.</p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
